import type { ReactNode } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';

export type ApplicationSectionIconColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

/** 36×36 wash tile — same sticker used by Digital footprint / Overview. */
export function ApplicationSectionIcon({
  children,
  color = 'primary',
}: {
  children: ReactNode;
  color?: ApplicationSectionIconColor;
}) {
  return (
    <Box
      sx={(t) => ({
        width: 36,
        height: 36,
        borderRadius: 1.5,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        color: t.palette[color].main,
        bgcolor: alpha(t.palette[color].main, t.palette.mode === 'light' ? 0.1 : 0.18),
      })}
    >
      {children}
    </Box>
  );
}

/** Icon + title (+ optional subtitle / end chips) for every application-detail section. */
export function ApplicationSectionHeader({
  title,
  subtitle,
  icon,
  iconColor = 'primary',
  endAdornment,
  mb = 1.5,
}: {
  title: string;
  subtitle?: string | null;
  icon: ReactNode;
  iconColor?: ApplicationSectionIconColor;
  endAdornment?: ReactNode;
  mb?: number;
}) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb }}>
      <ApplicationSectionIcon color={iconColor}>{icon}</ApplicationSectionIcon>
      <Box sx={{ minWidth: 0, flex: 1, pt: 0.15 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
            {title}
          </Typography>
          {endAdornment}
        </Stack>
        {subtitle ? (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.15,
              lineHeight: 1.4,
              color: 'text.primary',
              fontWeight: 500,
              opacity: 0.78,
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

/** Outlined paper + icon header — Overview / Device / Income / Employer pattern. */
export function ApplicationSectionCard({
  title,
  subtitle,
  icon,
  iconColor,
  children,
  endAdornment,
  action,
  onOpen,
  openLabel,
  paperSx,
  id,
  'data-testid': testId,
}: {
  title: string;
  subtitle?: string | null;
  icon: ReactNode;
  iconColor?: ApplicationSectionIconColor;
  children: ReactNode;
  endAdornment?: ReactNode;
  /** Right-aligned control next to the header (e.g. link button). */
  action?: ReactNode;
  /** Makes the card open a related surface (Overview → service tab). */
  onOpen?: () => void;
  openLabel?: string;
  paperSx?: SxProps<Theme>;
  /** DOM id, so other parts of the page can scroll this card into view. */
  id?: string;
  'data-testid'?: string;
}) {
  const interactive = typeof onOpen === 'function';
  return (
    <Paper
      variant="outlined"
      id={id}
      data-testid={testId}
      role={interactive ? 'link' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? (openLabel ? `Open ${openLabel}` : `Open ${title}`) : undefined}
      onClick={interactive ? onOpen : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      sx={[
        {
          p: 1.75,
          borderRadius: 2,
          minWidth: 0,
          height: '100%',
          ...(interactive
            ? {
                cursor: 'pointer',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: (t) => `0 6px 20px ${alpha(t.palette.primary.main, 0.1)}`,
                  bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.02 : 0.06),
                },
                '&:focus-visible': {
                  outline: (t) => `2px solid ${t.palette.primary.main}`,
                  outlineOffset: 2,
                },
              }
            : null),
        },
        ...(paperSx ? (Array.isArray(paperSx) ? paperSx : [paperSx]) : []),
      ]}
    >
      {action || (interactive && openLabel) ? (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ApplicationSectionHeader
              title={title}
              subtitle={subtitle}
              icon={icon}
              iconColor={iconColor}
              endAdornment={endAdornment}
              mb={0}
            />
          </Box>
          <Box sx={{ flexShrink: 0, pt: 0.25 }}>
            {action ?? (
              <Typography
                variant="caption"
                color="primary"
                sx={{ fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}
              >
                {openLabel} ›
              </Typography>
            )}
          </Box>
        </Stack>
      ) : (
        <ApplicationSectionHeader
          title={title}
          subtitle={subtitle}
          icon={icon}
          iconColor={iconColor}
          endAdornment={endAdornment}
          mb={1.5}
        />
      )}
      {children}
    </Paper>
  );
}
